import { useState, useEffect, useCallback } from 'react';
import { Ticket } from '../components/types/ticketTypes';
import { getAllTickets } from '../services/ticketDataService';

/**
 * Custom hook for managing ticket data from the API.
 * Returns { tickets, isLoading, error } following project conventions.
 */
export function useTicketData() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTickets = useCallback(async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllTickets(signal);
      setTickets(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Failed to load tickets';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadTickets(controller.signal);
    return () => controller.abort();
  }, [loadTickets]);

  const refresh = useCallback(() => {
    loadTickets();
  }, [loadTickets]);

  // Client-side filter helpers (operate on already-fetched tickets)
  const getTicketsByTechnician = useCallback(
    (name: string) => tickets.filter(t => t.assignedTo === name),
    [tickets]
  );

  const getTicketsByStatus = useCallback(
    (status: Ticket['status']) => tickets.filter(t => t.status === status),
    [tickets]
  );

  const getTicketsByPriority = useCallback(
    (priority: Ticket['priority']) => tickets.filter(t => t.priority === priority),
    [tickets]
  );

  const getUrgentTickets = useCallback(
    () => getTicketsByPriority('urgent'),
    [getTicketsByPriority]
  );

  const getOpenTickets = useCallback(
    () => getTicketsByStatus('open'),
    [getTicketsByStatus]
  );

  const searchTicketsLocal = useCallback(
    (term: string) => {
      const lower = term.toLowerCase();
      return tickets.filter(
        t =>
          t.title.toLowerCase().includes(lower) ||
          t.description.toLowerCase().includes(lower) ||
          t.customer.name.toLowerCase().includes(lower) ||
          t.customer.company.toLowerCase().includes(lower)
      );
    },
    [tickets]
  );

  return {
    tickets,
    isLoading,
    error,
    refresh,
    getTicketsByTechnician,
    getTicketsByStatus,
    getTicketsByPriority,
    getUrgentTickets,
    getOpenTickets,
    searchTickets: searchTicketsLocal,
  };
}

/**
 * Hook for getting a specific ticket by ID
 */
export function useTicket(ticketId: string) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const all = await getAllTickets(controller.signal);
        setTicket(all.find(t => t.id === ticketId) ?? null);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load ticket');
      } finally {
        setIsLoading(false);
      }
    };

    if (ticketId) {
      load();
    }

    return () => controller.abort();
  }, [ticketId]);

  return { ticket, isLoading, error };
}

/**
 * Hook for getting tickets by technician
 */
export function useTicketsByTechnician(technicianName: string) {
  const { tickets, isLoading, error } = useTicketData();
  const filtered = tickets.filter(t => t.assignedTo === technicianName);
  return { tickets: filtered, isLoading, error, count: filtered.length };
}
